import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { extractManagerMensagemInline, extractManagerMotivoTecnico } from 'src/app/core/utils/error.utils';
import { SharedModule } from 'src/app/shared/shared.module';
import { senhaForteValidator } from 'src/app/core/validators/senha.validators';
import { OtpAuthService, isRateLimitError } from 'src/app/core/services/otp-auth.service';
import {
  OTP_CONTA_JA_POSSUI_SENHA_STATE_KEY,
  OtpContaJaPossuiSenhaState
} from 'src/app/core/models/otp.model';
import { OtpCodigoStepController } from 'src/app/core/services/otp-codigo-step.controller';
import { OtpInputComponent } from 'src/app/shared/otp-input/otp-input.component';
import { OtpEmpresaSelecaoComponent } from 'src/app/shared/otp-empresa-selecao/otp-empresa-selecao.component';
import { FocusOnInitDirective } from 'src/app/shared/directives/focus-on-init.directive';

function senhasIguaisValidator(control: AbstractControl): ValidationErrors | null {
  const novaSenha = control.get('novaSenha')?.value;
  const confirmarSenha = control.get('confirmarSenha')?.value;
  return novaSenha && confirmarSenha && novaSenha !== confirmarSenha
    ? { senhasDivergentes: true }
    : null;
}

const FLUXO = 'esqueci-senha' as const;

/**
 * Fluxo "Esqueci a senha" com confirmação por código OTP (4 passos):
 * 1. Identificação (e-mail OU identificador — D9) [+ sub-estado: escolha de
 *    empresa, quando o dado resolve 2+ candidatos — `ux.md` §3.3/§5.12]
 * 2. Código de confirmação (NOVO)
 * 3. Nova senha
 * 4. Confirmação
 *
 * Os passos 1 e 2 (identificação + escolha de empresa + código) são a MESMA
 * máquina de estado de `primeiro-acesso` (spec §5) — vive em
 * `OtpCodigoStepController` pra não divergir entre os dois componentes de
 * novo. Só o passo 3 em diante (nova senha vs. criar senha + termos) é
 * específico daqui.
 *
 * Fonte da verdade: `.brain/tecnologia/features/reset-senha-otp/ux.md` +
 * `spec-tecnica.md`. Substitui o fluxo antigo de 3 passos
 * (`/validar` + `/redefinir`, sem prova de posse — GAP S-08).
 */
@Component({
  standalone: true,
  selector: 'app-esqueci-senha',
  templateUrl: './esqueci-senha.component.html',
  styleUrls: ['./esqueci-senha.component.scss'],
  providers: [MessageService],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    ButtonModule,
    InputTextModule,
    ToastModule,
    SharedModule,
    OtpInputComponent,
    OtpEmpresaSelecaoComponent,
    FocusOnInitDirective
  ]
})
export class EsqueciSenhaComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private otpAuthService = inject(OtpAuthService);

  @ViewChild(OtpInputComponent) private otpInputRef?: OtpInputComponent;

  step = 1;
  loading = false;
  errorMessage = '';
  showNovaSenha = false;
  showConfirmarSenha = false;

  private resetToken: string | null = null;

  /** Passos 1 (+ escolha de empresa) e 2 (código) — máquina compartilhada com `primeiro-acesso`. */
  readonly otpCode = new OtpCodigoStepController(
    FLUXO,
    this.fb,
    this.otpAuthService,
    this.messageService,
    {
      onAvancarParaCodigo: () => (this.step = 2),
      onCodigoValidado: (resetToken) => (this.resetToken = resetToken),
      onAvancarParaSenha: () => (this.step = 3),
      onVoltarParaIdentificacao: () => (this.step = 1),
      onError: (mensagem) => (this.errorMessage = mensagem),
      getOtpInput: () => this.otpInputRef
    },
    'Não foi possível validar os dados informados.',
    this.router
  );

  // ── Passo 3 — nova senha ───────────────────────────────────────────────────
  step3Form = this.fb.nonNullable.group(
    {
      novaSenha: ['', [Validators.required, senhaForteValidator()]],
      confirmarSenha: ['', [Validators.required]]
    },
    { validators: senhasIguaisValidator }
  );

  /**
   * Pré-preenche o passo 1 quando chega aqui redirecionada de
   * `primeiro-acesso` por já ter senha cadastrada (spec §4.6-B/§12-C item 4).
   * `getCurrentNavigation()` ainda é válido durante a ativação do componente
   * (mesma navegação); `history.state` é o fallback pra quando o `state` já
   * não está mais "corrente" (ex.: recarregar a página). Sem state nenhum,
   * comportamento atual — nenhum campo pré-preenchido.
   */
  ngOnInit(): void {
    const state = (this.router.getCurrentNavigation()?.extras.state ?? history.state) as
      | Record<string, unknown>
      | undefined;
    const contaJaPossuiSenha = state?.[OTP_CONTA_JA_POSSUI_SENHA_STATE_KEY] as
      | OtpContaJaPossuiSenhaState
      | undefined;

    if (contaJaPossuiSenha?.identificador) {
      this.otpCode.step1Form.controls.identificador.setValue(contaJaPossuiSenha.identificador);
    } else if (contaJaPossuiSenha?.email) {
      this.otpCode.step1Form.controls.email.setValue(contaJaPossuiSenha.email);
    }

    // Explica o redirecionamento — sem isto a pessoa só vê o campo preenchido,
    // sem entender por que caiu nesta tela (achado do @Peter, spec §12-C item 5).
    //
    // `setTimeout` NÃO é cosmético: `MessageService.add()` emite num `Subject`
    // sem replay. Chamado aqui dentro do `ngOnInit` deste componente, a emissão
    // acontece ANTES do `<p-toast>` filho ter rodado o próprio `ngOnInit` (onde
    // ele se inscreve no mesmo `Subject`) — a mensagem sai no vazio e some sem
    // erro nenhum. Adiar pro próximo tick garante que o `<p-toast>` já está
    // inscrito quando a mensagem é emitida.
    if (contaJaPossuiSenha?.mensagem) {
      const mensagem = contaJaPossuiSenha.mensagem;
      setTimeout(() => {
        this.messageService.add({
          severity: 'info',
          summary: 'Você já tem senha cadastrada',
          detail: mensagem,
          life: 8000
        });
      });
    }
  }

  ngOnDestroy(): void {
    this.otpCode.destroy();
  }

  /**
   * Erro sem tipo reconhecido — a caixa inline mostra no máximo o `mensagem`
   * estruturado do backend ou o fallback; o texto técnico cru (ex.: 404 de
   * rota, 500 sem envelope) vai só pro toast, nunca pra tela.
   */
  private tratarErroInesperado(err: unknown, fallbackInline: string): string {
    this.messageService.add({
      severity: 'error',
      summary: 'Erro inesperado',
      detail: extractManagerMotivoTecnico(err),
      life: 8000
    });
    return extractManagerMensagemInline(err, fallbackInline);
  }

  // ── Passo 3 — nova senha ────────────────────────────────────────────────────

  redefinirSenha(): void {
    if (this.step3Form.invalid || !this.resetToken) {
      this.step3Form.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    const { novaSenha } = this.step3Form.getRawValue();

    this.otpAuthService
      .redefinirSenha(this.resetToken, novaSenha)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: () => {
          this.step = 4;
          this.errorMessage = '';
        },
        error: (err) => {
          if (isRateLimitError(err)) {
            this.errorMessage = 'Muitas tentativas. Aguarde alguns minutos.';
          } else {
            this.errorMessage = this.tratarErroInesperado(
              err,
              'Não foi possível redefinir a senha. Tente novamente.'
            );
          }
        }
      });
  }

  toggleNovaSenha(): void {
    this.showNovaSenha = !this.showNovaSenha;
  }

  toggleConfirmarSenha(): void {
    this.showConfirmarSenha = !this.showConfirmarSenha;
  }

  irParaLogin(): void {
    this.router.navigate(['/login']);
  }

  voltarAoLogin(): void {
    this.router.navigate(['/login']);
  }
}
